import React, { useState, useContext, useEffect, useCallback } from 'react';
import Flag from 'react-flags';
import { displayTime, scaleUsersTimes } from './functions';
import { green, red } from '@mui/material/colors';
import { Accordion, AccordionSummary, AccordionDetails } from './Accordion';
import TabPanel from './TabPanel';
import CircularProgress from '@mui/material/CircularProgress';
import ConfigContext, { ITime } from '../../contexts/ConfigContext/ConfigContext';
import GameConfigContext from '../../contexts/GameConfigContext/GameConfigContext';
import { changeUserName, getBestTimes, IBestTime } from '../../api';
import DiffIcon, { diffLib } from './DiffIcon/DiffIcon';
import { useAlert } from 'react-alert';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { ChooseRecordsFilter, ChooseLevelOuter, NoGameButton, NoGameText, NoGamesBox, ChooseButtonGroup, ChooseLevelButton, TabsBox, AccordionBox, BestAccordionBox, SavingLoadingBox, EditUserInput, EditUserButton, UserName, UserNameBox, ChooseGameBox, GameRow, LastGamesBox, ChooseGameInner, ChooseButton, ChooseGameGroupHeader, ChooseGameGroupBox, ChooseGameButtonsBox, ProfileBox, ProfileName, Bomb, GameLevelName, GameLevelSize, GameLevelBombsCount, ShowStatsButton } from './ChooseGame.css';

const ChooseGame = (): JSX.Element => {
  const alert = useAlert();
  const { config, setConfig } = useContext(ConfigContext);
  const { gameConfig, createTiles, setGameConfig } = useContext(GameConfigContext);
  const [activeTab, setActiveTab] = useState(0);
  const [editingName, setEditingName] = useState(false);
  const [newUserName, setNewUserName] = useState(config.user.name);
  const [savingUserName, setSavingUserName] = useState(false);
  const [expanded, setExpanded] = React.useState<string | false>('panel1');
  const [bestUserLevel, setBestUserLevel] = useState<1 | 2 | 3>(1);
  const [recordsFilter, setRecordFilter] = useState('all');

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  useEffect(() => {
    void (async function() {
      const bestT = await getBestTimes(recordsFilter);
      bestT.best1 = scaleUsersTimes(bestT.best1);
      bestT.best2 = scaleUsersTimes(bestT.best2);
      bestT.best3 = scaleUsersTimes(bestT.best3);
      console.log(bestT);
      setConfig({...config, user: {...config.user, bestTimes: bestT}});
    })();
  }, [recordsFilter]);

  const handleStartStandardGame = useCallback((width: number, height: number, bombCount: number, difficult: 1 | 2 | 3) => {
    const newGmaeConfig = {...gameConfig};
    newGmaeConfig.areaTilesWidthNum = width;
    newGmaeConfig.areaTilesHeightNum = height;
    newGmaeConfig.bombCount = bombCount;
    newGmaeConfig.tileBoardWidth = newGmaeConfig.tileSize * width;
    newGmaeConfig.tileBoardHeight = newGmaeConfig.tileSize * height;
    newGmaeConfig.difficult = difficult;
    newGmaeConfig.flagCount = 0;
    newGmaeConfig.gameOver = false;
    newGmaeConfig.win = false;
    newGmaeConfig.started = false;
    newGmaeConfig.reloaded = !newGmaeConfig.reloaded;
    newGmaeConfig.tiles = createTiles(newGmaeConfig);
    setGameConfig(newGmaeConfig);
    // handleCloseChooser();
    setConfig({...config, openGameChooser: false});
  }, [gameConfig, config]);

  const handleChangeTab = useCallback((newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const saveNewUserName = useCallback(async () => {
    if (config.user.name === newUserName) {
      setEditingName(false);
      return;
    }
    setSavingUserName(true);
    const changed = await changeUserName(config.user.id, newUserName);
    if (changed.success) {
      console.log('save config user name', newUserName);
      setConfig({...config, user: {...config.user, name: newUserName}});
    } else {
      if (changed.exists) {
        alert.info('Username already exist');
      }
    }
    setSavingUserName(false);
    setEditingName(false);
  }, [newUserName, config]);

  const handleStartEditing = useCallback(() => {
    setEditingName(true);
  }, []);

  const handleCancelEditing = useCallback(() => {
    setNewUserName(config.user.name);
    setEditingName(false);
  }, [config.user.name]);

  const handleKeyDownInput = useCallback(async (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleCancelEditing();
    }
    if (event.key === 'Enter') {
      await saveNewUserName();
    }
  }, [newUserName, config.user.name]);

  const renderGameRow = useCallback((game: ITime) => <GameRow>
    <div><i title={game.date.toString().replace('T', ' ').slice(0, 19)} className="fa fa-info-circle" /> {displayTime(game.time)}</div>
    <div><DiffIcon diff={game.level} /></div>
  </GameRow>, []);

  const renderBestGameRow = useCallback((game: IBestTime, index: number) => <GameRow>
    <div style={{width:17}}>{index+1}</div>
    <div style={{display: 'flex', flex: 1}}>
      <Flag
        basePath="images"
        name={game.country}
        format="png"
        pngSize={16}
        shiny={true}
        alt=""
      />
      <span style={{marginLeft: 4, display: 'block', minWidth: 30}}>{game.name}</span>
    </div>
    <div style={{fontFamily: 'Verdana', fontSize: 12}}>
      {game.additional && <span style={{fontSize:11}} title={game.additional.map(x => displayTime(parseInt(x, 10))).join('\n')}>[+{game.additional.length}]</span>} {displayTime(game.time, true)}
    </div>
  </GameRow>, []);

  const handleChangeRecordsFilter = (filter: string) => {
    setRecordFilter(filter);
  };

  const showStats = () => {
    if (window.external) {
      window.external.notify('show-stats');
    }
  }

  return (
    <ChooseGameBox>
      <ChooseGameInner>
        <ChooseGameGroupBox>
          <ChooseGameGroupHeader>
            NEW GAME
          </ChooseGameGroupHeader>
          <ChooseGameButtonsBox>
            <ChooseButton variant="contained" color="success" onClick={() => handleStartStandardGame(9, 9, 10, 1)}>
              <GameLevelName style={{flex: 1}}>BEGINNER</GameLevelName>
              <GameLevelSize>9x9</GameLevelSize>
              <GameLevelBombsCount>10<Bomb className={'fa fa-bomb'} /></GameLevelBombsCount>
            </ChooseButton>
            <ChooseButton variant="contained" color="primary" onClick={() => handleStartStandardGame(16, 16, 40, 2)}>
              <GameLevelName style={{flex: 1}}>INTERMEDIATE</GameLevelName>
              <GameLevelSize>16x16</GameLevelSize>
              <GameLevelBombsCount>40<Bomb className={'fa fa-bomb'} /></GameLevelBombsCount>
            </ChooseButton>
            <ChooseButton variant="contained" color="error" onClick={() => handleStartStandardGame(30, 16, 99, 3)}>
              <GameLevelName style={{flex: 1}}>EXPERT</GameLevelName>
              <GameLevelSize>30x16</GameLevelSize>
              <GameLevelBombsCount>99<Bomb className={'fa fa-bomb'} /></GameLevelBombsCount>
            </ChooseButton>
          </ChooseGameButtonsBox>
          <BestAccordionBox>
            <ChooseGameGroupHeader>
              <ShowStatsButton>
                <Button color={'primary'} variant={'contained'} onClick={showStats}>SHOW STATS</Button>
              </ShowStatsButton>
              <div>RECORDS</div>
              <ChooseRecordsFilter>
                <ButtonGroup variant="contained">
                  <Button style={{color: recordsFilter !== 'all' ? '#666' : '#fff'}} color={recordsFilter === 'all' ? 'primary' : 'info'} variant={recordsFilter === 'all' ? 'contained' : 'text'} onClick={() => handleChangeRecordsFilter('all')}>ALL</Button>
                  <Button color={recordsFilter === 'all' ? 'info' : 'primary'} variant={recordsFilter === 'all' ? 'text' : 'contained'} onClick={() => handleChangeRecordsFilter(config.user.country)}>
                  <Flag
                    basePath="images"
                    name={config.user.country}
                    format="png"
                    pngSize={24}
                    shiny={true}
                    alt=""
                  />
                  </Button>
                </ButtonGroup>
              </ChooseRecordsFilter>
            </ChooseGameGroupHeader>
            <AccordionBox>
              <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                <AccordionSummary sx={{borderTopLeftRadius: 4, borderTopRightRadius: 4}} bgColor={diffLib[1].color}>BEGINNER</AccordionSummary>
                <AccordionDetails>
                  {config.user.bestTimes.best1.map(renderBestGameRow)}
                </AccordionDetails>
              </Accordion>
              <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
                <AccordionSummary bgColor={diffLib[2].color}>INTERMEDIATE</AccordionSummary>
                <AccordionDetails>
                  {config.user.bestTimes.best2.map(renderBestGameRow)}
                </AccordionDetails>
              </Accordion>
              <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')}>
                <AccordionSummary sx={expanded === 'panel3' ? {} : {borderBottomLeftRadius: 4, borderBottomRightRadius: 4}} bgColor={diffLib[3].color}>EXPERT</AccordionSummary>
                <AccordionDetails>
                  {config.user.bestTimes.best3.map(renderBestGameRow)}
                </AccordionDetails>
              </Accordion>
            </AccordionBox>
          </BestAccordionBox>
        </ChooseGameGroupBox>
        <ChooseGameGroupBox>
          <ChooseGameGroupHeader>
            <ProfileBox>
              {savingUserName && <SavingLoadingBox>
                <CircularProgress size={20} />
              </SavingLoadingBox>}
              <Flag
                basePath="images"
                name={config.user.country}
                format="png"
                pngSize={24}
                shiny={true}
                alt=""
              />
              <ProfileName>
                {editingName ?
                  <UserNameBox>
                    <EditUserInput value={newUserName} onChange={(e) => setNewUserName(e.target.value)} onKeyDown={handleKeyDownInput} />
                    <div style={{display: 'flex'}}>
                      <EditUserButton style={{marginRight: 4}} onClick={handleCancelEditing}><i style={{color: red[700]}} className="fa fa-window-close" /></EditUserButton>
                      <EditUserButton onClick={saveNewUserName}><i style={{color: green[700]}} className="fa fa-save" /></EditUserButton>
                    </div>
                  </UserNameBox>
                  :
                  <UserNameBox>
                    <UserName>{config.user.name}</UserName>
                    <EditUserButton onClick={handleStartEditing}><i style={{fontSize: 14}} className="fa fa-edit" /></EditUserButton>
                  </UserNameBox>
                }
              </ProfileName>
            </ProfileBox>
          </ChooseGameGroupHeader>
          <ChooseGameButtonsBox>
            <TabsBox>
              {/* <Tabs value={activeTab} onChange={handleChangeTab} aria-label="Tabs">
                <Tab label={<div><i className="fa fa-clock" /> Last</div>} {...a11yProps(0)} />
                <Tab label={<div><i className="fa fa-clock" /> Best</div>} {...a11yProps(1)} />
              </Tabs> */}
              <ChooseButtonGroup variant="contained" aria-label="choose-level" style={{marginBottom: 10}}>
                <ChooseLevelButton style={{padding: 10}} color={activeTab === 0  ? 'primary' : 'info'} onClick={() => handleChangeTab(0)}>LAST GAMES</ChooseLevelButton>
                <ChooseLevelButton style={{padding: 10}} color={activeTab === 1  ? 'primary' : 'info'} onClick={() => handleChangeTab(1)}>BEST TIMES</ChooseLevelButton>
              </ChooseButtonGroup>
            </TabsBox>
            <div>
              <TabPanel value={activeTab} index={0}>
                <LastGamesBox>
                  {config.user.lastGames.map((game) => <GameRow>
                    <div><i title={game.date.toString().replace('T', ' ').slice(0, 19)} className="fa fa-info-circle" /> {displayTime(game.time)}</div>
                    <div><DiffIcon diff={game.level} /></div>
                  </GameRow>)}
                </LastGamesBox>
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <LastGamesBox style={{justifyContent: 'center'}}>
                  <ChooseLevelOuter>
                    <ChooseButtonGroup variant="contained" aria-label="choose-level" style={{marginBottom: 10}}>
                      <ChooseLevelButton style={bestUserLevel !== 1 ? {background: '#ddd', color: '#777'} : {}} color={bestUserLevel === 1  ? 'success' : 'info'} onClick={() => setBestUserLevel(1)}>BEGINNER</ChooseLevelButton>
                      <ChooseLevelButton style={bestUserLevel !== 2 ? {background: '#ddd', color: '#777'} : {}} color={bestUserLevel === 2  ? 'primary' : 'info'} onClick={() => setBestUserLevel(2)}>INTERMEDIATE</ChooseLevelButton>
                      <ChooseLevelButton style={bestUserLevel !== 3 ? {background: '#ddd', color: '#777'} : {}} color={bestUserLevel === 3  ? 'error' : 'info'} onClick={() => setBestUserLevel(3)}>EXPERT</ChooseLevelButton>
                    </ChooseButtonGroup>
                  </ChooseLevelOuter>
                  {(bestUserLevel === 1 && config.user.best1.length === 0) && <NoGamesBox><NoGameText>NO GAMES AT BEGINNER LEVEL</NoGameText><NoGameButton onClick={() => handleStartStandardGame(9, 9, 10, 1)} size="small" variant="contained" color="success">PLAY NOW</NoGameButton></NoGamesBox>}
                  {(bestUserLevel === 2 && config.user.best2.length === 0) && <NoGamesBox><NoGameText>NO GAMES AT INTERMEDIATE LEVEL</NoGameText><NoGameButton onClick={() => handleStartStandardGame(16, 16, 40, 1)} size="small" variant="contained" color="primary">PLAY NOW</NoGameButton></NoGamesBox>}
                  {(bestUserLevel === 3 && config.user.best3.length === 0) && <NoGamesBox><NoGameText>NO GAMES AT EXPERT LEVEL</NoGameText><NoGameButton onClick={() => handleStartStandardGame(30, 16, 99, 3)} size="small" variant="contained" color="error">PLAY NOW</NoGameButton></NoGamesBox>}
                  {bestUserLevel === 1 && config.user.best1.map(renderGameRow)}
                  {bestUserLevel === 2 && config.user.best2.map(renderGameRow)}
                  {bestUserLevel === 3 && config.user.best3.map(renderGameRow)}
                </LastGamesBox>
              </TabPanel>
            </div>
          </ChooseGameButtonsBox>
        </ChooseGameGroupBox>
      </ChooseGameInner>
    </ChooseGameBox>
  );
};

export default ChooseGame;
